import { createFileRoute } from "@tanstack/react-router";
import Page from "@/pages/Blog";
import { getBlogPosts } from "@/lib/blog";

import { brand } from "@/config/brand";
const SITE_URL = brand.url;
const OG_IMAGE = `${SITE_URL}/__l5e/assets-v1/aabeccab-688e-44b7-96b1-aaf70d6a2a8d/social-share-cover.png`;
const OG_ALT = "Digitally Baffled LTD — UK Digital Transformation Consultancy";
const TITLE = "Blog — Digital Tips for Small Businesses | Digitally Baffled";
const DESC =
  "Plain-English guides and tips to help small businesses, tradespeople and startups navigate the digital world — SEO, AEO, GEO, social media, Google and more.";

export const Route = createFileRoute("/blog/")({
  head: () => {
    const posts = getBlogPosts();

    const blogJsonLd = {
      "@context": "https://schema.org",
      "@type": "Blog",
      "@id": `${SITE_URL}/blog#blog`,
      name: "Digitally Baffled Blog",
      description: DESC,
      url: `${SITE_URL}/blog`,
      inLanguage: "en-GB",
      publisher: {
        "@type": "Organization",
        "@id": `${SITE_URL}/#organization`,
        name: "Digitally Baffled LTD",
      },
      author: {
        "@type": "Person",
        "@id": `${SITE_URL}/#morgan-antell`,
        name: "Morgan Antell",
      },
      isPartOf: { "@id": `${SITE_URL}/#website` },
      blogPost: posts.slice(0, 10).map((post) => ({
        "@type": "BlogPosting",
        "@id": `${SITE_URL}/blog/${post.slug}#article`,
        headline: post.title,
        url: `${SITE_URL}/blog/${post.slug}`,
        datePublished: post.publishedAt,
        dateModified: post.updatedAt ?? post.publishedAt,
        ...(post.category ? { articleSection: post.category } : {}),
        ...(post.tags?.length ? { keywords: post.tags.join(", ") } : {}),
      })),
    };

    const collectionJsonLd = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "@id": `${SITE_URL}/blog#webpage`,
      name: TITLE,
      url: `${SITE_URL}/blog`,
      description: DESC,
      isPartOf: { "@id": `${SITE_URL}/#website` },
      about: { "@id": `${SITE_URL}/#organization` },
      inLanguage: "en-GB",
      breadcrumb: {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
          { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE_URL}/blog` },
        ],
      },
    };

    const itemListJsonLd = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Digitally Baffled Blog Posts",
      url: `${SITE_URL}/blog`,
      numberOfItems: posts.length,
      itemListElement: posts.map((post, i) => ({
        "@type": "ListItem",
        position: i + 1,
        item: {
          "@type": "BlogPosting",
          "@id": `${SITE_URL}/blog/${post.slug}#article`,
          headline: post.title,
          description: post.excerpt,
          url: `${SITE_URL}/blog/${post.slug}`,
          datePublished: post.publishedAt,
          dateModified: post.updatedAt ?? post.publishedAt,
          author: {
            "@type": "Person",
            "@id": `${SITE_URL}/#morgan-antell`,
            name: "Morgan Antell",
          },
          publisher: {
            "@type": "Organization",
            "@id": `${SITE_URL}/#organization`,
            name: "Digitally Baffled LTD",
          },
        },
      })),
    };

    const breadcrumbJsonLd = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE_URL}/blog` },
      ],
    };

    return {
      meta: [
        { title: TITLE },
        { name: "description", content: DESC },
        { property: "og:site_name", content: "Digitally Baffled" },
        { property: "og:title", content: TITLE },
        { property: "og:description", content: DESC },
        { property: "og:url", content: `${SITE_URL}/blog` },
        { property: "og:type", content: "website" },
        { property: "og:image", content: OG_IMAGE },
        { property: "og:image:secure_url", content: OG_IMAGE },
        { property: "og:image:type", content: "image/png" },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:image:alt", content: OG_ALT },
        { property: "og:locale", content: "en_GB" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:site", content: "@digitallybaffld" },
        { name: "twitter:creator", content: "@digitallybaffld" },
        { name: "twitter:title", content: TITLE },
        { name: "twitter:description", content: DESC },
        { name: "twitter:image", content: OG_IMAGE },
        { name: "twitter:image:alt", content: OG_ALT },
      ],
      links: [{ rel: "canonical", href: `${SITE_URL}/blog` }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(blogJsonLd) },
        { type: "application/ld+json", children: JSON.stringify(collectionJsonLd) },
        { type: "application/ld+json", children: JSON.stringify(itemListJsonLd) },
        { type: "application/ld+json", children: JSON.stringify(breadcrumbJsonLd) },
      ],
    };
  },
  component: Page,
});
